import { useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Field } from "./Checkout";

const EMPTY = { name: "", email: "", phone: "", event_date: "", treat_type: "", quantity: "", letters_to_spell: "", theme: "", budget: "", notes: "" };
const TREATS = ["Dipped Strawberries", "Letter / Number Box", "Gift Box with Flowers", "Cake / Cupcakes", "Cake Mix", "Something else"];

function PhotoPicker({ photo, setPhoto }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image must be under 8MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/inspiration", fd);
      setPhoto({ ...data, preview: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(errMsg(err));
    }
    setUploading(false);
  };
  return (
    <label className="flex cursor-pointer items-center gap-4 rounded-2xl border-2 border-dashed border-white/20 p-4 transition-colors hover:border-[var(--pink)]" data-testid="inspiration-upload">
      {photo ? <img src={photo.preview} alt="Inspiration" className="h-20 w-20 rounded-xl object-cover" data-testid="inspiration-preview" />
        : <span className="flex h-20 w-20 items-center justify-center rounded-xl bg-white/5">{uploading ? <Loader2 className="animate-spin" /> : <ImagePlus className="text-[var(--pink)]" />}</span>}
      <span className="text-sm text-white/70">{photo ? "Photo added. Tap to change." : "Add an inspiration photo (optional)"}</span>
      <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="inspiration-file-input" />
    </label>
  );
}

export default function CustomOrder() {
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/custom-requests", { ...form, photo_path: photo?.path || null });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  if (done)
    return (
      <div className="rise mx-auto max-w-2xl px-4 py-24 text-center" data-testid="custom-order-thanks">
        <Heart className="mx-auto h-14 w-14 fill-[var(--pink)] text-[var(--pink)]" />
        <h1 className="mt-6 text-4xl sm:text-5xl">Request received!</h1>
        <p className="mt-3 text-white/70">Thank you, {form.name.split(" ")[0]}. We'll reach out by email or phone with a quote soon.</p>
        <Link to="/shop" className="btn-candy mt-8" data-testid="custom-thanks-shop-button">Browse the shop</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6" data-testid="custom-order-page">
      <p className="font-script text-xl text-[var(--pink)]">Made just for you</p>
      <h1 className="text-5xl sm:text-6xl">Request a Custom Order</h1>
      <p className="mt-4 max-w-xl text-white/70">Tell us about your celebration and we'll follow up with a personal quote.</p>
      <form onSubmit={submit} className="panel mt-10 space-y-5 p-6 sm:p-8" data-testid="custom-order-form">
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Name *"><input required className="field" value={form.name} onChange={set("name")} data-testid="custom-name-input" /></Field>
          <Field label="Email *"><input required type="email" className="field" value={form.email} onChange={set("email")} data-testid="custom-email-input" /></Field>
          <Field label="Phone *"><input required type="tel" minLength={7} className="field" value={form.phone} onChange={set("phone")} data-testid="custom-phone-input" /></Field>
        </div>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Event date"><input type="date" className="field" value={form.event_date} onChange={set("event_date")} data-testid="custom-event-date-input" /></Field>
          <Field label="Treat type">
            <select className="field" value={form.treat_type} onChange={set("treat_type")} data-testid="custom-treat-type-select">
              <option value="">Choose one</option>
              {TREATS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Quantity"><input className="field" placeholder="e.g. 2 dozen" value={form.quantity} onChange={set("quantity")} data-testid="custom-quantity-input" /></Field>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Colors / theme"><input className="field" placeholder="e.g. pink & blue gender reveal" value={form.theme} onChange={set("theme")} data-testid="custom-theme-input" /></Field>
          <Field label="Budget"><input className="field" placeholder="e.g. $80-$120" value={form.budget} onChange={set("budget")} data-testid="custom-budget-input" /></Field>
        </div>
        <Field label="Letters / word to spell (for letter boxes)"><input className="field uppercase" placeholder="e.g. HAPPY BDAY, MOM, 30" value={form.letters_to_spell} onChange={set("letters_to_spell")} data-testid="custom-letters-input" /></Field>
        <Field label="Notes"><textarea rows={4} className="field" placeholder="Anything else we should know?" value={form.notes} onChange={set("notes")} data-testid="custom-notes-input" /></Field>
        <PhotoPicker photo={photo} setPhoto={setPhoto} />
        <button disabled={busy} className="btn-candy w-full sm:w-auto" data-testid="custom-submit-button">{busy ? "Sending..." : "Send my request"}</button>
      </form>
    </div>
  );
}
